
import { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';

interface PEOProviderSearchProps {
  initialProvider?: string;
  onSelectProvider: (providerName: string) => void;
  readOnly?: boolean;
}

export const PEOProviderSearch = ({ initialProvider, onSelectProvider, readOnly = false }: PEOProviderSearchProps) => {
  const [peoProviders, setPeoProviders] = useState<{id: string, name: string}[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredProviders, setFilteredProviders] = useState<{id: string, name: string}[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
  const [showProvidersList, setShowProvidersList] = useState(false);

  // Fetch PEO providers when the component mounts
  useEffect(() => {
    const fetchPEOProviders = async () => {
      try {
        const { data: providers, error } = await supabase
          .from('peo_providers')
          .select('id, name')
          .order('name');
          
        if (error) {
          console.error('Error fetching PEO providers:', error);
        } else {
          setPeoProviders(providers || []);
        }
      } catch (error) {
        console.error('Error in PEO providers fetch:', error);
      }
    };
    
    fetchPEOProviders();
  }, []);

  // Filter providers based on search term
  useEffect(() => {
    setShowProvidersList(searchTerm.trim() !== '');
    
    if (searchTerm.trim() === '') {
      setFilteredProviders([]);
    } else {
      const filtered = peoProviders.filter(provider => 
        provider.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredProviders(filtered);
    }
  }, [searchTerm, peoProviders]);

  // Initialize with the passed provider
  useEffect(() => {
    if (initialProvider) {
      setSearchTerm(initialProvider);
      setSelectedProvider(initialProvider);
    }
  }, [initialProvider]);

  const selectProvider = (providerName: string) => {
    setSearchTerm(providerName);
    setSelectedProvider(providerName);
    onSelectProvider(providerName);
    setShowProvidersList(false);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    onSelectProvider(value);
  };

  return (
    <div className="space-y-2">
      <Label htmlFor="current_provider">Current PEO Provider</Label>
      
      <div className="relative">
        <Input
          id="current_provider"
          placeholder="Search for a PEO provider"
          value={searchTerm}
          onChange={handleSearchChange}
          onFocus={() => setShowProvidersList(!readOnly)}
          className="w-full"
          disabled={readOnly}
        />
        
        {!readOnly && showProvidersList && filteredProviders.length > 0 && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
            <ul>
              {filteredProviders.map((provider) => (
                <li 
                  key={provider.id}
                  className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                  onClick={() => selectProvider(provider.name)}
                >
                  {provider.name}
                </li>
              ))}
              <li 
                className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-t border-gray-200"
                onClick={() => selectProvider("Other (not listed)")}
              >
                Other (not listed)
              </li>
            </ul>
          </div>
        )}
      </div>
      
      {!readOnly && selectedProvider === 'Other (not listed)' && (
        <div className="mt-2">
          <Label htmlFor="current_provider_other">Specify other PEO provider</Label>
          <Input
            id="current_provider_other"
            placeholder="Enter your current PEO provider"
            onChange={(e) => onSelectProvider(e.target.value)}
            disabled={readOnly}
          />
        </div>
      )}
    </div>
  );
};
